<script>
    import { FloatingMenu, Notifications } from '@repo/ui';
    import FloatingMenuUserInfoRow from '#account/FloatingMenuUserInfoRow.svelte';
    import { getInitials } from '@repo/config/Utils';
    import { translationActive, translationRollover } from '@repo/stores/Language';
    import {
        showSubscriptionsModal,
        showUserSettingsModal,
        showTranslationSettingsModal,
    } from '@repo/stores/Actions';
    import { goto } from '$app/navigation';
    import { onDestroy, onMount } from 'svelte';

    let {
        // Required deps — each app wires these to its own Auth/Settings layer.
        getCurrentUser,
        signOut,
        hasAction,
        hasRole,
        actions,
        sections,
        roles,
        headerImage,

        // Routing. homeHref wins when the user can't access Manage.
        homeHref = '/',
        manageHref = '/manage',
        classroomHref = '/classroom',

        // Which top-nav tab is active. Consumers compute this from $page when
        // they have multiple sections (Education); a constant is fine for
        // single-section apps (Admin, TAP).
        currentSection = undefined,

        // Optional: resolves the "should I go to classroom instead of manage?"
        // branch for district/school admins who also have classes. Omit in
        // apps without classrooms (TAP).
        getUserClasses = null,

        // Optional: forwarded into the Notifications component. Omit to hide
        // notifications entirely.
        markNotificationAsRead = null,

        // Feature toggles. Defaults match Education.
        showManageLink = true,
        showNotifications = true,
        showTranslationRollover = true,

        // Override the user-menu items + action handler. Defaults match the
        // existing Education/Admin menu (settings, translation, sign out).
        menuItems = null,
        onMenuAction = null,

        // Slot for the meilisearch (or other) search control. Rendered inside
        // the manage-access gate alongside the Manage link.
        searchSnippet = null,

        // App switcher. Pass the list of apps the user can access (shaped as
        // `{ id, icon, title }[]`) and a handler. The switcher button only
        // renders when there's at least one *other* app to switch to. The
        // consumer is responsible for fetching this list (e.g. via
        // @repo/firebase's getUserApps) and mapping APP_META.
        availableApps = null,
        currentApp = null,
        onSwitchApp = null,

        // Slot for trailing modals (SubscriptionsModal, UserSettingsModal,
        // TranslationSettingsModal, etc.). Consumer decides what to include.
        children = null,
    } = $props();

    const otherApps = $derived(
        Array.isArray(availableApps)
            ? availableApps.filter((a) => a && a.id && a.id !== currentApp)
            : []
    );

    const effectiveSection = $derived(currentSection ?? sections.MANAGE);

    const defaultMenuItems = [
        { icon: 'settings', label: 'user settings', action: 'settings' },
        { icon: 'translate', label: 'translation settings', action: 'translation-settings' },
        { label: 'Sign Out', icon: 'logout', action: 'sign-out' },
    ];

    const resolvedMenuItems = $derived(menuItems ?? defaultMenuItems);

    let headerElement = $state();
    let showMenu = $state(false);
    let showAppSwitcher = $state(false);
    let appSwitcherBtn = $state();
    // Anchored to the button rather than a fixed offset, which drifted as the
    // header's right-hand controls changed per app.
    let appSwitcherPos = $state({ top: '4rem', right: '1rem' });
    let userAvatar = $state();
    let userName = $state(null);
    let userInitials = $derived(getInitials(userName));
    let isInputFocused = $state(false);
    let forceRerender = $state(0);

     
    const unsubscribeUser = getCurrentUser().subscribe((userData) => {
        if (userData) {
            const userDisplayName = (userData) => {
                userName = null;
                if (userData.providerData && userData.providerData.length > 0) {
                    const providerData = userData.providerData[0];
                    const systemData = userData.systemData;
                    if (
                        providerData.displayName &&
                        providerData.displayName.indexOf('{') === 0
                    ) {
                        try {
                            const parsed = JSON.parse(providerData.displayName);
                            if (parsed && parsed.first && parsed.last) {
                                return parsed.first + ' ' + parsed.last;
                            }
                        } catch (e) {
                            console.error(e);
                        }
                    } else if (systemData?.displayName) {
                        return systemData.displayName;
                    }

                    if (providerData.email) {
                        return providerData.email;
                    }
                }

                return (
                    userData.systemData?.displayName ||
                    userData.systemData?.email ||
                    'User'
                );
            };

            if (userData.providerData && userData.providerData.length > 0) {
                userAvatar = userData.providerData[0].photoURL || null;
                userName = userDisplayName(userData);
            } else {
                userAvatar = userData.systemData?.photoURL || null;
                userName =
                    userData.systemData?.displayName ||
                    userData.systemData?.email ||
                    'User';
            }
        }

        // Hack to force rerender
        forceRerender++;
    });

    function handleMainMenuClick() {
        showMenu = !showMenu;
        if (showMenu) {
            document.addEventListener('click', handleDocumentClick);
        }
    }

    function handleAppSwitcherClick() {
        showAppSwitcher = !showAppSwitcher;
        if (showAppSwitcher) {
            const rect = appSwitcherBtn?.getBoundingClientRect();
            if (rect) {
                appSwitcherPos = {
                    top: `${rect.bottom + 10}px`,
                    right: `${Math.max(8, window.innerWidth - rect.right)}px`,
                };
            }
            document.addEventListener('click', handleAppSwitcherDocumentClick);
        }
    }

    function handleAppTileClick(id) {
        showAppSwitcher = false;
        onSwitchApp?.(id);
    }

    function handleAppSwitcherDocumentClick(e) {
        if (!e.target.classList.contains('app-switcher-btn')) {
            showAppSwitcher = false;
            document.removeEventListener('click', handleAppSwitcherDocumentClick);
        }
    }

    function defaultMenuAction(action) {
        switch (action) {
            case 'sign-out':
                signOut();
                break;
            case 'subscriptions':
                showSubscriptionsModal.set(true);
                break;
            case 'settings':
                showUserSettingsModal.set(true);
                break;
            case 'translation-settings':
                showTranslationSettingsModal.set(true);
                break;
        }
    }

    function handleMainMenuActionClick(e) {
        showMenu = false;
        const action = e.detail.action;
        if (onMenuAction) {
            onMenuAction(action);
        } else {
            defaultMenuAction(action);
        }
    }

    function handleDocumentClick(e) {
        if (!e.target.classList.contains('menu-btn')) {
            showMenu = false;
            document.removeEventListener('click', handleDocumentClick);
        }
    }

    function toggleTranslationRollover() {
        translationRollover.set(!$translationRollover);
    }

    function hasAccessToManage() {
        return showManageLink && hasAction(actions.VIEW_MANAGE);
    }

    function handleKeyPress(e) {
        if (e.key === 'Enter') {
            toggleTranslationRollover();
        }
    }

    onMount(() => {
        handleWindowResize();
    });

    onDestroy(() => {
        try {
            unsubscribeUser();
        } catch (err) {
            console.error(err);
        }
    });

    let clicked = false;
    async function clickHome() {
        if (clicked) {
            return;
        }
        clicked = true;
        try {
            if (hasAction(actions.VIEW_MANAGE) && !hasRole(roles.TEACHER)) {
                if (
                    getUserClasses &&
                    (hasRole(roles.DISTRICT_ADMIN) || hasRole(roles.SCHOOL_ADMIN))
                ) {
                    const cls = await getUserClasses();
                    if (cls.length > 0) {
                        goto(classroomHref);
                        return;
                    }
                }
                goto(manageHref);
            } else {
                goto(homeHref);
            }
        } finally {
            clicked = false;
        }
    }

    let previousWidth = null;
    function handleWindowResize() {
        const screenWidth = window.innerWidth;

        if (previousWidth === screenWidth) {
            return;
        }

        if (!headerElement) {
            return;
        }

        // Threshold for when to move the menu button is 500px.
        if (
            previousWidth === null ||
            (screenWidth <= 500 && previousWidth > 500) ||
            (screenWidth > 500 && previousWidth <= 500)
        ) {
            previousWidth = screenWidth;
            const rollover = headerElement.querySelector(
                '.translation-rollover'
            )?.parentElement;
            const menuButton =
                headerElement.querySelector('.menu-btn')?.parentElement;

            const ul = headerElement.querySelector('.header-right');

            if (!rollover || !menuButton || !ul) {
                return;
            }

            if (screenWidth <= 500) {
                ul.insertBefore(menuButton, rollover);
            } else {
                ul.insertBefore(rollover, menuButton);
            }
        }
    }
</script>

<svelte:window onresize={handleWindowResize} />

{#if userName}
    {#key forceRerender}
        <header bind:this={headerElement} class="header">
            <h1 class="header-left">
                <!-- svelte-ignore a11y_invalid_attribute -->
                <a
                    onclick={clickHome}
                    href="javascript:;"
                    class="header-button interactive"
                    title="Home To Dashboard"
                >
                    <i class="material-icons" aria-hidden="true">home</i>
                    <img
                        class="header-logo"
                        src="/tenant/{headerImage}"
                        alt="Biozone world home page"
                    />
                </a>
            </h1>
            <ul class="header-right">
                {#if showTranslationRollover && $translationActive}
                    <li class="translation-rollover-item">
                        <div class="translation-rollover">
                            <div class="text">
                                <label for="translation-toggle">
                                    Translation Rollover
                                </label>
                                <span class="discription">Toggle On/Off</span>
                            </div>
                            <span
                                class="switch"
                                onclick={toggleTranslationRollover}
                                onkeydown={handleKeyPress}
                            >
                                <input
                                    type="checkbox"
                                    id="translation-toggle"
                                    checked={$translationRollover}
                                    onfocus={() => (isInputFocused = true)}
                                    onblur={() => (isInputFocused = false)}
                                />

                                <span
                                    class="slider round"
                                    class:focus-visible={isInputFocused}
                                    role="checkbox"
                                    aria-checked={$translationRollover}
                                ></span>
                            </span>
                        </div>
                    </li>
                {/if}
                {#if hasAccessToManage()}
                    <li class="manage-item">
                        <a
                            data-section={sections.MANAGE}
                            class="header-menu-item header-menu-link interactive {effectiveSection ===
                            sections.MANAGE
                                ? 'active'
                                : ''}"
                            href={manageHref}
                        >
                            <span>Manage</span>
                        </a>
                    </li>
                    {#if searchSnippet}
                        <li class="search-item" style="padding-left: 1.5rem;">
                            {@render searchSnippet()}
                        </li>
                    {/if}
                {/if}
                {#if showNotifications}
                    <li class="notification-item" style="padding-left: 1.5rem;">
                        <Notifications markNotificationAsRead={markNotificationAsRead} />
                    </li>
                {/if}
                {#if otherApps.length > 0}
                    <li class="app-switcher-item">
                        <button
                            bind:this={appSwitcherBtn}
                            onclick={handleAppSwitcherClick}
                            class="app-switcher-btn material-icons header-menu-item interactive"
                            class:open={showAppSwitcher}
                            title="Switch app"
                            aria-controls="app-switcher-menu"
                            aria-expanded={showAppSwitcher}
                        >
                            apps
                        </button>
                    </li>
                {/if}
                <li class="user-menu-item">
                    <button
                        onclick={handleMainMenuClick}
                        class="menu-btn material-icons header-menu-item interactive"
                        title="Main Menu"
                        aria-controls="user-menu"
                        aria-expanded={showMenu}
                    >
                        <span>
                            {userInitials}
                        </span>
                    </button>
                </li>
            </ul>

            {#if showAppSwitcher && otherApps.length > 0}
                <FloatingMenu
                    close={() => (showAppSwitcher = false)}
                    items={[]}
                    top={appSwitcherPos.top}
                    right={appSwitcherPos.right}
                    id="app-switcher-menu"
                    as="div"
                >
                    <div class="app-switcher">
                        <p class="app-switcher-title">Switch to</p>
                        <ul class="app-tiles" class:single={otherApps.length === 1}>
                            {#each otherApps as app (app.id)}
                                <li>
                                    <button
                                        class="app-tile interactive"
                                        onclick={() => handleAppTileClick(app.id)}
                                    >
                                        <span class="material-icons" aria-hidden="true">{app.icon}</span>
                                        <span class="app-tile-name">{app.title}</span>
                                    </button>
                                </li>
                            {/each}
                        </ul>
                    </div>
                </FloatingMenu>
            {/if}

            {#if showMenu}
                <FloatingMenu
                    on:click={handleMainMenuActionClick}
                    close={() => (showMenu = false)}
                    items={resolvedMenuItems}
                    top="1rem"
                    right="1rem"
                    id="user-menu"
                    asMenu={true}
                    as="div"
                >
                    <FloatingMenuUserInfoRow as="div" />
                </FloatingMenu>
            {/if}
        </header>
    {/key}
    {#if children}
        {@render children()}
    {/if}
{/if}

<style>
    .header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        background-color: #000;
        z-index: 1200;
    }

    .header-left {
        display: flex;
        align-items: center;
        padding: 1rem 0.25rem;
        margin-right: auto;
    }

    .header-right {
        display: flex;
        align-items: center;
        flex-direction: row;
        justify-self: start;

        grid-column: 2;
        grid-row: 2;
    }

    .header-right li {
        display: inline-block;
    }

    .header-button {
        display: flex;
        padding: 0.5rem 0.75rem 0.5rem 0.1rem;

        border-radius: 0.25rem;
        align-items: center;
    }

    .header-button i {
        display: flex;
        color: white;
        margin: 0 0.75rem 0 0.5rem;
    }

    .header-button:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }

    .header-logo {
        width: 11rem;
    }

    @media (max-width: 500px) {
        .header {
            display: grid;
            grid-template-columns: 1fr auto;
            grid-template-rows: auto;
            align-items: center;
            padding: 0 1rem;
        }

        .header-left {
            grid-column: 1;
            display: flex;
            align-items: center;
            padding: 1rem 0.25rem;
            margin-right: auto;
        }

        .header-right {
            grid-column: 1;
            grid-row: 2;
            justify-self: start;
            display: flex;
            align-items: center;
            width: 100%;
            position: unset;
        }

        .notification-item {
            position: absolute;
            right: 80px;
            top: 20px;
        }

        /* Menu button aligned to the right */
        .user-menu-item {
            grid-column: 2 / 3;
            grid-row: 1 / 2;
            justify-self: end;
            margin-right: 1rem;
            padding: 1rem 0.25rem;

            position: absolute;
            top: 0;
            right: 10px;
        }

        .user-menu-item .menu-btn {
            margin-right: 0;
        }

        .user-menu-item img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        /* Translation rollover toggle on the next row */
        .translation-rollover-item {
            grid-column: 1 / 3;
            grid-row: 2 / 3;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            padding: 0.5rem 0;
        }
    }

    .header-menu-item {
        margin-left: 1.5rem;
        color: #fff;
    }

    .header-menu-link {
        line-height: 2rem;
        border-bottom: solid 2px rgba(0, 0, 0, 0);
    }

    .header-menu-link:hover,
    .header-menu-link.active {
        border-bottom: solid 2px white;
    }

    .header-menu-link > span {
        font-weight: 500;
        font-size: 0.9rem;
        text-transform: uppercase;
    }

    .menu-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.2rem;
        height: 2.2rem;
        margin-right: 1.5rem;
        border-radius: 0.5rem;
        color: #262424;
        font-weight: 600;
        font-size: 1rem;
        background-color: rgb(255, 206, 44);
    }

    .app-switcher-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.2rem;
        height: 2.2rem;
        border-radius: 0.5rem;
        color: #fff;
        font-size: 1.4rem;
        background-color: transparent;
        transition: background-color 0.2s;
    }

    .app-switcher-btn:hover,
    .app-switcher-btn.open {
        background-color: rgba(255, 255, 255, 0.1);
    }

    .app-switcher {
        padding: 0.75rem;
        min-width: 15rem;
    }

    .app-switcher-title {
        margin: 0.25rem 0.25rem 0.6rem;
        text-transform: uppercase;
        font-weight: 600;
        font-size: 0.7rem;
        letter-spacing: 0.06em;
        color: rgb(255, 206, 44);
    }

    .app-tiles {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.4rem;
    }

    /* One app in a two-column grid reads as a stray half-width row. */
    .app-tiles.single {
        grid-template-columns: 1fr;
    }

    .app-tile {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.4rem;
        padding: 0.9rem 0.5rem;
        border: 1px solid rgba(255, 255, 255, 0.06);
        border-radius: 0.35rem;
        background-color: rgba(255, 255, 255, 0.03);
        color: #fff;
        cursor: pointer;
        transition:
            background-color 0.15s,
            border-color 0.15s;
    }

    .app-tile:hover {
        background-color: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 206, 44, 0.5);
    }

    .app-tile:focus-visible {
        outline: 2px solid rgb(255, 206, 44);
        outline-offset: 1px;
    }

    .app-tile .material-icons {
        font-size: 1.6rem;
        color: rgb(255, 206, 44);
    }

    .app-tile-name {
        font-size: 0.8rem;
        font-weight: 600;
        line-height: 1.2;
        text-align: center;
    }

    .menu-btn span {
        color: inherit;
        font-size: inherit;
        font-weight: inherit;
        text-transform: inherit;
        pointer-events: none;
    }

    .translation-rollover {
        color: white;
        display: grid;
        grid-template-columns: 1fr 35px;
        width: 200px;
        padding: 0.4rem 1rem;
        margin-right: 0.1rem;
    }

    .translation-rollover .text > span,
    .translation-rollover .text > label {
        width: 100%;
        display: inline-block;
        font-size: 0.75rem;
    }

    .translation-rollover .text label {
        color: rgb(255, 206, 44);
    }

    .translation-rollover .text .description {
        color: #fff;
    }

    .switch {
        position: relative;
        display: inline-block;
        width: 35px;
        height: 20px;
        align-self: center;
    }

    .switch input {
        opacity: 0;
        width: 0;
        height: 0;
    }

    .slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #ccc;
        transition:
            background-color 0.4s,
            transform 0.4s;
    }

    .slider:before {
        position: absolute;
        content: '';
        height: 14px;
        width: 14px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        transition:
            background-color 0.4s,
            transform 0.4s;
    }

    .slider.focus-visible {
        outline: 2px solid rgb(255, 255, 255);
    }

    input:checked + .slider {
        background-color: rgb(73, 50, 227);
    }

    input:checked + .slider:before {
        transform: translateX(14px);
    }

    .slider.round {
        border-radius: 34px;
    }

    .slider.round:before {
        border-radius: 50%;
    }
</style>
